import React, { createContext, useMemo, useContext, useState } from 'react';
import useSiteData from '../../hooks/useSiteData';
import type {
  CartTenantDetails,
  ExpressCheckoutContextProviderProps,
  ExpressCheckoutContextValues,
} from '../../types';

const ExpressCheckoutContext = createContext<
  ExpressCheckoutContextValues | undefined
>(undefined);
const ExpressCheckoutContextProvider = ({
  children,
}: ExpressCheckoutContextProviderProps) => {
  const [isDynamicLoadFailure, setIsDynamicLoadFailure] = useState(false);
  const {
    vistacartTenant,
    siteName,
    externalAppNavigation,
    paymentEnvironment,
    siteCountry,
    geoData,
    addressAutocompleteData,
    addressValidationData,
    uiLocalization,
  } = useSiteData();

  const expressCheckoutContextValues = useMemo(() => {
    const cartTenantDetails: CartTenantDetails = {
      tenantId: vistacartTenant,
      siteName,
      absoluteCheckoutUrl: externalAppNavigation.checkout,
      absoluteCartUrl: externalAppNavigation.cart,
      absolutePrivacyPolicyUrl: externalAppNavigation.privacyPolicy,
      absoluteTermsAndConditionsUrl: externalAppNavigation.termsAndConditions,
      absoluteDeliveryUrl: externalAppNavigation.delivery,
      paymentEnvironment,
      absoluteOrderDetailsUrl: externalAppNavigation.orderDetails,
      siteCountry,
      allowedShippingCountries: geoData.shipping || [],
      allowedBillingCountries: geoData.billing || [],
      forbidPoBoxCountries: geoData.noPoBoxCountries || [],
      addressValidationData,
      addressAutocompleteData,
      language: uiLocalization.language,
    };

    return {
      cartTenantDetails,
      isDynamicLoadFailure,
      setIsDynamicLoadFailure,
    };
  }, [
    externalAppNavigation,
    geoData,
    paymentEnvironment,
    siteCountry,
    siteName,
    addressValidationData,
    addressAutocompleteData,
    uiLocalization,
    vistacartTenant,
    isDynamicLoadFailure,
  ]);

  return (
    <ExpressCheckoutContext.Provider value={expressCheckoutContextValues}>
      {children}
    </ExpressCheckoutContext.Provider>
  );
};

const useExpressCheckoutContext = () => {
  const context = useContext(ExpressCheckoutContext);
  if (context === undefined) {
    throw new Error(
      'useExpressCheckoutContext must be used within an ExpressCheckoutContextProvider'
    );
  }
  return context;
};

export {
  ExpressCheckoutContext,
  ExpressCheckoutContextProvider,
  useExpressCheckoutContext,
};
